import { ChangeSetListener } from "./ChangeSet";
import { DataProvider } from "./DataProvider";

export type DataProviderName =
    | "users"
    | "places"
    | "geometry"
    | "filtered-places"
    | "moderation-requests"
    | "countries"
    | "ground-water"
    | "journals"
    | "warnings"
    | "user-filters"
    | "public-filters"
    | "place-filters"
    | "place-report"
    | "place-media"
    | "place-list"
    | "place-list-entries";

export type DataSource = {
    source: DataProviderName;
    paths: string[];
};

export class DataClient {
    constructor(
        public readonly owner: ChangeSetListener,
        public readonly sources: DataSource[]
    ) {}
}

export class LocalDataStore {
    private _sources = new Map<string | DataProviderName, DataProvider>();
    private _clients: DataClient[] = [];

    constructor() {}

    public addSource(provider: DataProvider) {
        this._sources.set(provider.name, provider);
    }

    public getSource<T = DataProvider>(name: DataProviderName): T {
        return this._sources.get(name) as T;
    }

    public connectClient(client: DataClient) {
        this._clients.push(client);
        client.sources.forEach((x) => {
            const source = this.getSource(x.source);
            if (source) {
                source.addListener(client.owner, x.paths);
            }
        });
    }

    public disconnectClient(client: object) {
        this._clients = this._clients.filter((x) => x.owner !== client);
    }

    public async create(sourceName: DataProviderName, props: any, ...args: any[]) {
        return this._dispatch(sourceName, "create", props, ...args);
    }

    public async update(sourceName: DataProviderName, props: any, ...args: any[]) {
        return this._dispatch(sourceName, "update", props, ...args);
    }

    public async delete(sourceName: DataProviderName, id: string) {
        return this._dispatch(sourceName, "delete", id);
    }

    public read(sourceName: DataProviderName, id: string) {
        return this._dispatch(sourceName, "read", id);
    }

    private _dispatch(sourceName: DataProviderName, op: string, props: any, ...args: any[]) {
        const source = this._sources.get(sourceName);
        if (source) {
            return (source as any)[op](props, ...args);
        }
    }
}
